<button
  class="relative overflow-visible"
  mat-icon-button
  type="button"
  matTooltip="Approval inbox"
  aria-label="Approval inbox"
  (click)="openPanel()"
  #inboxOrigin>
  <mat-icon class="relative z-0" [svgIcon]="'heroicons_outline:inbox-stack'"></mat-icon>
  @if (pendingCount > 0) {
    <span
      class="pointer-events-none absolute -top-0.5 left-0 right-0 z-20 flex h-4 items-center justify-center">
      <span
        class="ml-4 flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-primary px-1 text-xs font-medium leading-none text-white">
        {{ pendingCount > 99 ? '99+' : pendingCount }}
      </span>
    </span>
  }
</button>

<ng-template #inboxPanel>
  <div
    class="fixed inset-0 flex flex-col overflow-hidden bg-card shadow-lg sm:static sm:inset-auto sm:w-140 sm:min-w-140 sm:rounded-2xl">
    <!-- Header -->
    <div class="flex shrink-0 items-center bg-primary px-5 py-4 text-on-primary">
      <div class="-ml-1 mr-2 sm:hidden">
        <button mat-icon-button type="button" (click)="closePanel()">
          <mat-icon class="text-current icon-size-5" [svgIcon]="'heroicons_solid:x-mark'"></mat-icon>
        </button>
      </div>
      <div class="min-w-0">
        <div class="text-lg font-semibold leading-tight">Approval Inbox</div>
        <div class="text-xs text-on-primary/80">
          {{ pendingCount }} pending
        </div>
      </div>
      <div class="ml-auto">
        <button
          mat-icon-button
          type="button"
          matTooltip="Refresh"
          [disabled]="loading"
          (click)="refresh()">
          <mat-icon class="text-current icon-size-5" [svgIcon]="'heroicons_outline:arrow-path'"></mat-icon>
        </button>
      </div>
    </div>

    <!-- Search -->
    <div class="shrink-0 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
      <mat-form-field
        class="fuse-mat-dense w-full"
        subscriptSizing="dynamic"
        floatLabel="never">
        <mat-icon
          matPrefix
          class="icon-size-5 text-secondary"
          [svgIcon]="'heroicons_outline:magnifying-glass'"></mat-icon>
        <input
          matInput
          [(ngModel)]="filterKeyword"
          placeholder="Search documents"
          aria-label="Search approvals"
          (ngModelChange)="onKeywordChange()"
          (keyup.enter)="loadItems()" />
        @if (filterKeyword) {
          <button
            mat-icon-button
            matSuffix
            type="button"
            aria-label="Clear search"
            (click)="clearKeyword()">
            <mat-icon class="icon-size-4" [svgIcon]="'heroicons_outline:x-mark'"></mat-icon>
          </button>
        }
      </mat-form-field>
    </div>

    <!-- Content -->
    <div class="relative flex min-h-0 flex-auto flex-col overflow-y-auto px-4 pb-4 pt-3 sm:max-h-120">
      @if (loading) {
        <div class="flex flex-auto items-center justify-center py-12">
          <mat-spinner diameter="32"></mat-spinner>
        </div>
      } @else {
        @for (item of items; track trackByRow($index, item)) {
          <div
            class="group relative mb-2.5 shrink-0 cursor-pointer overflow-hidden rounded-xl border border-gray-200 bg-card shadow-sm transition hover:border-primary/40 dark:border-gray-700 dark:bg-gray-900/40 dark:hover:border-primary/40"
            (click)="openDocument(item)">
            <div class="flex items-start gap-3 px-3.5 py-3">
              <div
                class="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <mat-icon
                  class="icon-size-5 text-current"
                  [svgIcon]="'heroicons_outline:document-text'"></mat-icon>
              </div>

              <div class="min-w-0 flex-1">
                <div class="flex items-start gap-2">
                  <div class="min-w-0 flex-1">
                    <div class="truncate text-sm font-semibold leading-5 text-gray-900 dark:text-white">
                      {{ item.documentNumber || 'Untitled document' }}
                    </div>
                    <p class="mt-0.5 truncate text-sm leading-5 text-gray-500 dark:text-gray-400">
                      {{ item.transactionTypeName || item.moduleName || '—' }}
                      @if (item.pendingStatusName) {
                        <span> · {{ item.pendingStatusName }}</span>
                      } @else if (item.stepOrder != null) {
                        <span> · Step {{ item.stepOrder }}</span>
                        @if (inboxStepLabel(item); as stepLabel) {
                          <span> ({{ stepLabel }})</span>
                        }
                      }
                    </p>
                    @if (item.companyName) {
                      <span
                        class="mt-1 inline-flex max-w-full truncate rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
                        {{ item.companyName }}
                      </span>
                    }
                  </div>
                  @if (item.amount != null) {
                    <div class="shrink-0 text-xs font-semibold tabular-nums text-gray-700 dark:text-gray-200">
                      {{ item.amount | amount }}
                    </div>
                  }
                </div>

                <div class="mt-2 flex flex-wrap items-center justify-between gap-2">
                  <div class="text-xs text-gray-400 dark:text-gray-500 truncate min-w-0">
                    {{ item.submittedByName || '—' }}
                    @if (item.submittedAt) {
                      <span> · {{ item.submittedAt | companyDate: 'datetime' }}</span>
                    }
                  </div>
                  @if (canAct(item)) {
                    <div class="flex shrink-0 items-center gap-1" (click)="$event.stopPropagation()">
                      <button
                        type="button"
                        class="rounded-full border border-red-200 bg-card px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-400 dark:border-red-900/50 dark:text-red-400"
                        (click)="reject(item)">
                        Reject
                      </button>
                      <button
                        type="button"
                        class="rounded-full border border-primary/30 bg-primary px-2.5 py-1 text-xs font-medium text-on-primary transition hover:opacity-90"
                        (click)="approve(item)">
                        Approve
                      </button>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        }

        @if (!items.length) {
          <div class="flex flex-auto flex-col items-center justify-center px-8 py-12">
            <div
              class="flex h-14 w-14 flex-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-600">
              <mat-icon
                class="text-primary-700 dark:text-primary-50"
                [svgIcon]="'heroicons_outline:inbox-stack'"></mat-icon>
            </div>
            <div class="mt-5 text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
              No pending approvals
            </div>
            <div class="mt-1 max-w-60 text-center text-sm text-gray-500 dark:text-gray-400">
              You're all caught up. New items will appear here when they need your action.
            </div>
          </div>
        }
      }
    </div>

    <!-- Footer -->
    <div class="shrink-0 border-t px-5 py-3 dark:border-gray-700">
      <button
        type="button"
        class="text-primary text-sm font-medium underline"
        (click)="openFullInbox()">
        Open full inbox
      </button>
    </div>
  </div>
</ng-template>
